/**
 * Centralized API base URLs.
 *
 * Flip {@link USE_PROD_API_URLS} once — every service host updates together.
 *
 * | Export                 | Key        | Used by                                      |
 * |------------------------|------------|----------------------------------------------|
 * | AUTH_API_BASE          | auth       | device-id, OTP, refresh-token, guest token   |
 * | FARE_ENGINE_API_BASE   | fareEngine | commute fare calculator                      |
 * | OFFER_API_BASE         | offer      | Find a Ride `/rides`, `/laterrides`          |
 * | CORE_API_BASE          | offer      | publish/book, ride transactions              |
 * | RIDE_DETAILS_API_BASE  | offer      | `/ride-details`, booking lookup              |
 * | CHALO_API_BASE         | offer      | profiles, support, delete-account (legacy)   |
 * | VEHICLE_API_BASE       | offer      | `/makes`, `/models`                          |
 * | MARKETING_API_BASE     | offer      | employee referral marketing                  |
 * | ROUTES_ADMIN_API_BASE  | admin      | waiting guests / available hosts             |
 */

/**
 * `true`  → production (`duri-prod-*`)
 * `false` → test / legacy (`auth-server`, `chaloapp-*`)
 */
export const USE_PROD_API_URLS = true;

type ApiBaseUrls = {
  auth: string;
  fareEngine: string;
  offer: string;
  admin: string;
};

/** Test stack (Azure Container Apps) */
const API_BASE_URLS_TEST: ApiBaseUrls = {
  auth: "https://duri-test-auth-api.greenmushroom-6e319197.eastasia.azurecontainerapps.io",
  fareEngine: "https://duri-test-fe-api.greenmushroom-6e319197.eastasia.azurecontainerapps.io",
  offer: "https://duri-test-server-api.greenmushroom-6e319197.eastasia.azurecontainerapps.io",
  admin: "https://duri-test-admin-api.greenmushroom-6e319197.eastasia.azurecontainerapps.io",
};

/** Production stack */
const API_BASE_URLS_PROD: ApiBaseUrls = {
  auth: "https://duri-prod-auth-apiapp.azurewebsites.net",
  fareEngine: "https://duri-prod-fe-api.jollysky-bd2991cd.centralindia.azurecontainerapps.io",
  offer: "https://duri-prod-core-apiapp.azurewebsites.net",
  admin: "https://duri-prod-admin-api.jollysky-bd2991cd.centralindia.azurecontainerapps.io",
};

function normalizeBaseUrl(url: string): string {
  return url.trim().replace(/\/+$/, "");
}

function selectApiBaseUrls(): ApiBaseUrls {
  const source = USE_PROD_API_URLS ? API_BASE_URLS_PROD : API_BASE_URLS_TEST;
  return {
    auth: normalizeBaseUrl(source.auth),
    fareEngine: normalizeBaseUrl(source.fareEngine),
    offer: normalizeBaseUrl(source.offer),
    admin: normalizeBaseUrl(source.admin),
  };
}

const urls = selectApiBaseUrls();

/** Auth — device-id, request-otp, validate-otp, refresh-token */
export const AUTH_API_BASE = urls.auth;

/** Fare engine — calculate-seat-fare */
export const FARE_ENGINE_API_BASE = urls.fareEngine;

/** Offer / Find a Ride — GET /rides, POST /laterrides */
export const OFFER_API_BASE = urls.offer;

/** Core ride APIs — publish_or_book_ride, RideTransaction (same host as offer) */
export const CORE_API_BASE = urls.offer;

/** Public ride-info / booking-info — POST /ride-details */
export const RIDE_DETAILS_API_BASE = urls.offer;

/**
 * Profile / support / delete-account / vehicle / marketing
 * share the offer host in both stacks.
 */
export const CHALO_API_BASE = urls.offer;
export const VEHICLE_API_BASE = urls.offer;
export const MARKETING_API_BASE = urls.offer;

/** Admin — RoutesInformation (waiting guests, available hosts) */
export const ROUTES_ADMIN_API_BASE = urls.admin;

/** Optional FAQ discussion backend (unused unless set later). */
export const FAQ_DISCUSSION_API_BASE = "";

/** Guest token phone for public pages (commute calculator, ride-info). */
export const GUEST_AUTH_PHONE = "9381570951";
